import Moment from "moment";
const timeZoneAbbreviations: { [key: string]: string } = {
  "Eastern Standard Time": "(EST)",
  "Eastern Daylight Time": "(EDT)",
  "Pacific Standard Time": "(PST)",
  "Pacific Daylight Time": "(PDT)",
  "India Standard Time": "(IST)",
  "Central Standard Time": "(CST)",
  "Alaska Standard Time": "(AKST)",
  "Hawaii-Aleutian Standard Time": "(HST)",
  "Mountain Standard Time": "(MST)",
};

export const extractTime = (dateTime: Date | null | undefined): string => {
  if (!dateTime) {
    return "";
  }
  const momentDateTime = Moment(dateTime);
  if (!momentDateTime.isValid()) {
    return "";
  }
  return momentDateTime.format("hh:mm A");
};

export const addMinutesToTime = (
  time: string | null | undefined,
  minutes: number | null | undefined,
) => {
  if (!time) {
    return "";
  }
  const momentTime = Moment(time, "hh:mm A");
  if (!momentTime.isValid()) {
    return "";
  }
  momentTime.add(minutes, "minutes");
  return momentTime.format("hh:mm A");
};

export const subtractMinutesFromTime = (
  time: string | null | undefined,
  minutes: number | null | undefined,
) => {
  if (!time) {
    return "";
  }
  const momentTime = Moment(time, "hh:mm A");
  if (!momentTime.isValid()) {
    return "";
  }
  momentTime.subtract(minutes, "minutes");
  return momentTime.format("hh:mm A");
};

export const getTimezonedAbbreviations = (
  timezone: string,
): string | undefined => {
  const timezoneAbbreviations: Record<string, string> = {
    // List of common time zone abbreviations and their full names
    AKST: "America/Anchorage",
    PST: "America/Los_Angeles",
    MST: "America/Denver",
    CST: "America/Chicago",
    EST: "America/New_York",
    GMT: "GMT",
    UTC: "UTC",
    CET: "Europe/Paris",
    EET: "Europe/Istanbul",
    IST: "Asia/Kolkata",
    JST: "Asia/Tokyo",
    AEST: "Australia/Sydney",
    PET: "America/Honolulu",
    HST: "America/Adak",
    NZST: "Pacific/Auckland",
    NZDT: "Pacific/Auckland",
  };

  const match = timezone.match(/\(([^)]+)\)/);
  return match ? match[1] : undefined;
};

export function getTimeZoneAbbreviation(
  datetime: Date,
  timeZoneNameFormat: "short" | "long" = "long",
): string {
  const options: Intl.DateTimeFormatOptions = {
    timeZoneName: timeZoneNameFormat,
  };
  const longTimeZone = datetime
    .toLocaleDateString("en-US", options)
    .split(", ")[1];
  return timeZoneAbbreviations[longTimeZone] || longTimeZone;
}
export function formatDateTimeZone(
  dateTime: string,
  timezone?: string,
): string {
  if (!dateTime) {
    return "";
  }
  const datetime = new Date(dateTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: timezone || "UTC",
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour12: true,
    timeStyle: "short",
    timeZone: timezone || "UTC",
  };

  const formattedDate = datetime.toLocaleDateString("en-US", dateOptions);
  const formattedTime = datetime.toLocaleTimeString("en-US", timeOptions);

  const timezoneAbbreviation =
    getTimezonedAbbreviations(timezone || "UTC") || timezone || "UTC";

  return `${formattedDate} ${formattedTime} (${timezoneAbbreviation})`;
}

export function formatDateTime(dateTime: string): string {
  if (!dateTime) {
    return "";
  }
  const datetime = new Date(dateTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour12: true,
    timeStyle: "short",
    timeZone: "UTC",
  };

  const formattedDate = datetime.toLocaleString("en-US", dateOptions);
  const formattedTime = datetime.toLocaleString("en-US", timeOptions);

  const timeZoneAbbreviation = getTimeZoneAbbreviation(datetime);

  return `${formattedDate} ${formattedTime} ${timeZoneAbbreviation}`;
}

export function formatDateTimeToLocal(dateTime: string): string {
  if (!dateTime || isNaN(new Date(dateTime).getTime())) {
    return "-";
  }

  const datetime = new Date(dateTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const formattedDate = datetime.toLocaleString("en-US", dateOptions);
  const formattedTime = datetime.toLocaleString("en-US", timeOptions);

  const timeZoneAbbreviation = getTimeZoneAbbreviation(datetime);

  return `${formattedDate} ${formattedTime} ${timeZoneAbbreviation}`;
}

export function formatDateTimeToLocalNoTz(dateTime: string): string {
  if (!dateTime || isNaN(new Date(dateTime).getTime())) {
    return "-";
  }

  const datetime = new Date(dateTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const formattedDate = datetime.toLocaleString("en-US", dateOptions);
  const formattedTime = datetime.toLocaleString("en-US", timeOptions);

  return `${formattedDate} ${formattedTime}`;
}

export function getLocalDateString(dateTime: Date): string {
  const datetime = new Date(dateTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  const formattedDate = datetime.toLocaleString("en-US", dateOptions);

  return `${formattedDate}`;
}

export const formateDate = (date: string, timeZone?: string) => {
  if (!date) {
    return "-";
  }
  if (date.length > 0) {
    const datetime = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    if (timeZone) {
      options.timeZone = timeZone;
    }
    return datetime.toLocaleString("en-US", options);
  }
  return "";
};

export const formatDateStringToLocalDate = (dateString: string) => {
  const appointmentDate = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  return new Intl.DateTimeFormat("en-US", options).format(appointmentDate);
};

export function formatDateStringToLocalTime(dateTime: string): string {
  const datetime = new Date(dateTime);

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const formattedTime = datetime.toLocaleString("en-US", timeOptions);

  return formattedTime;
}

export const formateDateToYYYYMMDD = (date: Date) => {
  if (!date) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  return date.toLocaleString("en-US", options);
};
